import { User } from '@/models'
import { AUTHENTICATED, push } from '@/lib/gtm'

export default ({ app: { $auth, $logger, $toast, $echo, router }, store }) => {
  return {

    user () {
      return $auth.$state.user
    },
    /**
     * Initialise the users authentication events. This should only be called once.
     * @returns {Promise<void>}
     */
    initializeStateWatchers () {
      store.watch(
        // watch the user object. This changes on login / logout
        state => state.auth.user,
        (val, oldValue) => {
          // if they were previously logged out and are logging in
          if (!oldValue && val) {
            // we delay the private channel listener registration otherwise it messes up on sign up and we increases load time
            setTimeout(() => {
              // we need to set the Authorization bearer token
              $echo.connector.pusher.config.auth.headers.Authorization = window.localStorage.getItem('auth._token.local')
              // and then setup our pusher channels
              this.attachChannelListeners()
              // authenticated event
              push(AUTHENTICATED, {
                firstName: $auth.$state.user.firstName,
                lastName: $auth.$state.user.lastName,
                email: $auth.$state.user.email,
                userAuthenticated: true,
                userId: $auth.$state.user.email,
                userType: $auth.$state.user.type,
                userUuid: $auth.$state.user.id,
                credit: $auth.$state.user.stats.creditAvailable
              })
            },
            // This number is arbitrary
            2500
            )

            // user is logged in logging out
          } else if (!val && oldValue) {
            // we destruct the pusher channels
            new User(oldValue).destroy()
            // we clear the bugsnag user
          }
          // If they are logging in or this is being refreshed for whatever reason
          if (val) {
            const activeBooking = val.stats.bookingActive
            // if the authenticated user has an active booking in the stats data, we set the session
            // if they previously did have one for the same booking id, we don't re-trigger it otherwise
            // the modal will pop up again
            const lastActiveBookingMatches = activeBooking && oldValue &&
              oldValue.stats.bookingActive &&
              oldValue.stats.bookingActive.id === activeBooking.id
            // if new active booking and not last
            if (activeBooking && !lastActiveBookingMatches) {
              // If the user is NOT in the session
              const isInSession = val.id === activeBooking.learnerId ? activeBooking.isLearnerInSession : activeBooking.isAdvisorInSession
              if (!isInSession) {
                store.commit('myKintell/SET_AWAITING_BOOKING_SESSION', activeBooking)
              }
            }

            // Check for completed booking pending review
            const awaitingReview = val.stats.bookingAwaitingReview
            store.commit('myKintell/SET_AWAITING_BOOKING_REVIEW', awaitingReview)
          }
        },
        {
          immediate: true
        }
      )
    },
    async signOut () {
      // destroy channel
      await $auth.logout()
      await $auth.reset()
      $auth.$storage.setUniversal('redirect', '/')
      store.dispatch('myKintell/reset')
      router.replace('/')
      window.location.href = '/'
    },
    async signIn (email, password, redirect = true, data = {}) {
      $logger.info('Logging in as user', email)

      await $auth.loginWith('local', {
        auth: {
          username: email,
          password
        },
        data
      })

      store.dispatch('myKintell/reset')

      if (redirect) {
        // if there is a redirect link go to that otherwise go home
        router.push($auth.$state.redirect || '/')
      }
    },

    async signInSocial (service, code, callback, options = {}) {
      const data = {
        ...options,
        code,
        callback
      }

      try {
        await $auth.loginWith(service, {
          data
        })
      } catch (e) {
        if (e.response && e.response.data) {
          $toast.error(e.response.data.error)
        } else {
          $logger.error(e)
        }
        return false
      }

      // Nuxt Auth package uses the name of strategy as the token key and there is not an easy way to change this
      const token = window.localStorage.getItem('auth._token.' + service)
      // Our system checks for the key local in our local storage
      window.localStorage.setItem('auth._token.local', token)
      store.dispatch('myKintell/reset')
      return true
    },
    attachChannelListeners () {
      const channel = new User($auth.$state.user).privateChannel
        // Kintell card approvals
        .listen('.App\\Events\\KintellCard\\ReviewApprovedEvent', this.handleReviewApprovedEvent)
        .listen('.App\\Events\\KintellCard\\ReviewRejectedEvent', this.handleReviewRejectEvent)
        // Invite sign ups
        .listen('.App\\Events\\User\\ClaimedInvite', this.handleInvitedUserSignedUp)
        // Handle booking status updates
        .listen('.App\\Events\\Booking\\Status\\Confirmed', this.handleAdvisorConfirmedBooking)
        .listen('.App\\Events\\Booking\\Status\\PendingApproval', this.handleNewBookingRequest)
        .listen('.App\\Events\\Booking\\Status\\CancelledProvisional', this.handleProvisionalBookingCancelled)
        .listen('.App\\Events\\Booking\\Status\\CancelledConfirmed', this.handleConfirmedBookingCancelled)
        .listen('.App\\Events\\Booking\\Status\\ProvisionalRequestExpired', this.handleProvisionalBookingExpired)
        .listen('.App\\Events\\Booking\\Status\\SuggestedTimes', this.handleProvisionalBookingSuggestedTimes)
        .listen('.App\\Events\\Booking\\Reminder\\StartsInTenMinutes', this.handleBookingStartsInTenMinutes)
        .listen('.App\\Events\\User\\StripeIndividualVerificationStatus\\Pending', this.handleVerificationPending)
        .listen('.App\\Events\\User\\StripeIndividualVerificationStatus\\Verified', this.handleVerificationVerified)
        .listen('.App\\Events\\User\\StripeIndividualVerificationStatus\\Unverified', this.handleVerificationUnverified)
        // @todo Either delete this or implement. Not doing anything at the moment
        .listen('.App\\Events\\Booking\\AdvisorSetOvertime', this.handleAdvisorSetOvertime)
      $logger.trace('Attaching channel listeners', channel)
    },
    handleVerificationPending (e) {
      $logger.info('handleVerificationPending', e)
      store.dispatch('auth/refresh')
    },
    handleVerificationVerified (e) {
      $logger.info('handleVerificationVerified', e)
      store.dispatch('auth/refresh')
    },
    handleVerificationUnverified (e) {
      $logger.info('handleVerificationUnverified', e)
      store.dispatch('auth/refresh')
    },
    handleInvitedUserSignedUp (e) {
      $logger.info('Handle invited user signed up event', e)
      $toast.success(e.user.firstName + ' ' + e.user.lastName + ' has just signed up with your invite!', {
        duration: 10000
      })
      store.dispatch('myKintell/reloadInvites')
      store.dispatch('auth/refresh')
    },
    handleReviewRejectEvent (e) {
      $logger.info('Handle review rejected event', e)
      $toast.success(
        `The Kintell Team has left comments have been left on your Kintell Card "${e.kintellCard.title}".`,
        { duration: 10000 }
      )
      // we need to check if the user has rejected the card we're looking at
      const kintellCard = store.get('kintellCards/activeKintellCard')
      if (kintellCard && kintellCard.id === e.kintellCard.id) {
        kintellCard.status = e.kintellCard.status
        store.commit('kintellCards/SET_ACTIVE_KINTELL_CARD', kintellCard)
      }
      store.dispatch('myKintell/reloadKintellCards')
      store.dispatch('auth/refresh')
    },
    handleReviewApprovedEvent (e) {
      $logger.info('Handle review approved event', e)
      $toast.success(
        `Your Kintell Card "${e.kintellCard.title}" has been approved!`,
        { duration: 10000 }
      )
      // we need to check if the user has rejected the card we're looking at
      const kintellCard = store.get('kintellCards/activeKintellCard')
      if (kintellCard && kintellCard.id === e.kintellCard.id) {
        kintellCard.status = e.kintellCard.status
        store.commit('kintellCards/SET_ACTIVE_KINTELL_CARD', kintellCard)
      }
      $auth.fetchUser()
      store.dispatch('myKintell/reloadKintellCards')
    },
    // both the learner and advisor receive this, in the case of the learner this is sent either instantly or after
    // the booking payment has gone through
    handleNewBookingRequest (e) {
      $logger.info('Handle new booking request event', e)
      const isAdvisor = e.booking.advisorId === $auth.$state.user.id
      // If createdBy is an object then get the id from the object
      const createdById = e.booking.createdBy.id ? e.booking.createdBy.id : e.booking.createdBy
      const bookingInitiatedByLearner = createdById === e.booking.learnerId
      if (isAdvisor && bookingInitiatedByLearner) {
        $toast.success('You have just been sent a booking request!', {
          duration: 10000
        })
        store.dispatch('myKintell/reloadBookingRequestsAdvising')
        store.dispatch('auth/refresh')
      }
    },
    handleProvisionalBookingSuggestedTimes (e) {
      $logger.info('Handle advisor suggest booking time event', e)
      let message = 'An advisor you requested a booking with has suggested some alternative times.'
      if (e.whoRescheduled) {
        message = 'One of your confirmed bookings has been rescheduled.'
      }
      $toast.success(message, {
        duration: 10000
      })
      store.dispatch('myKintell/maybeReloadActiveBooking', e)
      store.dispatch('myKintell/reloadBookings')
      store.dispatch('auth/refresh')
    },
    handleAdvisorConfirmedBooking (e) {
      $logger.info('Handle advisor confirmed booking event', e)

      const whoConfirmed = e.booking.confirmedBy === e.booking.learnerId
        ? e.booking.learner.firstName + ' ' + e.booking.learner.lastName
        : e.booking.advisor.firstName + ' ' + e.booking.advisor.lastName
      $toast.success(`${whoConfirmed} has just confirmed a booking with you!`, {
        duration: 10000
      })
      store.dispatch('myKintell/maybeReloadActiveBooking', e)
      store.dispatch('myKintell/reloadBookings')
      store.dispatch('auth/refresh')
    },
    // @todo Either delete this or implement. Not doing anything at the moment
    handleAdvisorSetOvertime (e) {
      $logger.info('Handle advisor set overtime event', e)
      if (e.booking.billForOvertime) {
        store.dispatch('meeting/addActionMessage', 'timeExceededLearner')
      } else {
        store.dispatch('meeting/addSystemMessage', `${e.booking.advisor.firstName + ' ' + e.booking.advisor.lastName} is not charging for further time.`)
      }
    },
    handleBookingStartsInTenMinutes (e) {
      $logger.info('Handle Booking Starts In Ten Minutes event', e)
      store.commit('myKintell/SET_AWAITING_BOOKING_SESSION', e.booking)
    },
    handleProvisionalBookingCancelled (e) {
      const isAdvisor = e.booking.advisorId === $auth.$state.user.id
      const otherUser = isAdvisor ? e.booking.learner : e.booking.advisor
      $toast.success(`${otherUser.firstName + ' ' + otherUser.lastName} has cancelled your provisional booking with them.`, {
        duration: 10000
      })
      store.dispatch('myKintell/maybeReloadActiveBooking', e)
      store.dispatch('myKintell/reloadBookings')
      store.dispatch('auth/refresh')
    },
    handleConfirmedBookingCancelled (e) {
      const isAdvisor = e.booking.advisorId === $auth.$state.user.id
      const otherUser = isAdvisor ? e.booking.learner : e.booking.advisor
      $toast.success(`${otherUser.firstName + ' ' + otherUser.lastName} has cancelled your confirmed booking with them.`, {
        duration: 10000
      })
      store.dispatch('myKintell/maybeReloadActiveBooking', e)
      store.dispatch('myKintell/reloadBookings')
      store.dispatch('auth/refresh')
    },
    handleProvisionalBookingExpired (e) {
      $toast.success('Your provisional booking has expired.', {
        duration: 10000
      })

      // if the user is on the booking page, redirect them to bookings dashboard
      if (store.get('myKintell/activeBooking') && e.booking.id === store.get('myKintell/activeBooking').id) {
        router.push('/my-kintell/bookings')
      }
      store.dispatch('myKintell/reloadBookings')
      store.dispatch('auth/refresh')
    }
  }
}
